import axios from "axios";
import { useEffect, useState, useCallback, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import WelcomeComponent from "../../../day-1/components/welcomeComponent";
import { API_ENDPOINT } from "../../../../../../lib/api-url";
import { useOperatorActions } from "../../../../../../utils/useOperatorActions";
import AnswerContainerComponent from "./container/AnswerContainerComponent";
// ⬇️ make sure this path points to the file above
import useSteadyCountdown from "../../../../../../hooks/useSteadyCountDown";
import QuesionAnswersComponent from "./quesionAnswersComponent";

const listVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.48, delayChildren: 0.21 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

const WELCOME_DELAY_MS = 3000;

const WS_URL = process.env.REACT_APP_QUESTIONBROADCAST_SOCKET_URL || "";
const WS_TOKEN =
  process.env.REACT_APP_QUESTIONBROADCAST_TOKEN || "client_secret_token";

export default function QuestionComponent() {
  const [selectedId, setSelectedId] = useState(null);
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [startTimeToken, setStartTimeToken] = useState(null);
  const [currentTimeToken, setCurrentTimeToken] = useState(null);
  const [finished, setFinished] = useState(false);
  const [highlightedAnswerId, setHighlightedAnswerId] = useState(null);
  const [showUserAnswers, setShowUserAnswers] = useState(false);
  const [userAnswers, setUserAnswers] = useState([]);

  // [FIX] Keep latest values in refs so callbacks don't force effect restarts
  const questionRef = useRef(null);
  const selectedIdRef = useRef(null);
  useEffect(() => {
    questionRef.current = question;
  }, [question]);
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  // [FIX] Reliable countdown + detailed logging
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [endAtMs, setEndAtMs] = useState(null);
  const tickTimeoutRef = useRef(null);

  const start = useCallback((durationSec) => {
    const dur = Math.max(0, Number(durationSec) || 0);
    if (tickTimeoutRef.current) {
      clearTimeout(tickTimeoutRef.current);
      tickTimeoutRef.current = null;
    }
    setSecondsLeft(dur);
    const target = Date.now() + dur * 1000;
    setEndAtMs(target);
    console.log("[TIMER] start()", { durationSec: dur, endAtMs: target });
  }, []);

  const stop = useCallback(() => {
    if (tickTimeoutRef.current) {
      clearTimeout(tickTimeoutRef.current);
      tickTimeoutRef.current = null;
    }
    if (endAtMs != null) {
      console.log("[TIMER] stop()", { endAtMs });
    }
    setEndAtMs(null);
  }, [endAtMs]);

  useEffect(() => {
    if (endAtMs == null) {
      console.log("[TIMER] effect: endAtMs is null, no ticking");
      return;
    }
    console.log("[TIMER] effect: scheduling ticks", { endAtMs });

    const tick = () => {
      const now = Date.now();
      const msLeft = Math.max(0, endAtMs - now);
      const next = Math.ceil(msLeft / 1000);
      setSecondsLeft((prev) => {
        if (prev !== next) console.log("[TIMER] tick", { now, msLeft, next });
        return next;
      });
      if (msLeft > 0) {
        const remainder = msLeft % 1000;
        const delay = remainder ? remainder : 1000;
        tickTimeoutRef.current = setTimeout(tick, Math.min(250, delay));
      } else {
        console.log("[TIMER] reached zero");
        tickTimeoutRef.current = null;
      }
    };

    tick();

    return () => {
      if (tickTimeoutRef.current) {
        clearTimeout(tickTimeoutRef.current);
        tickTimeoutRef.current = null;
      }
      console.log("[TIMER] cleanup for endAtMs", { endAtMs });
    };
  }, [endAtMs]);
  // [/FIX]

  const [canReveal, setCanReveal] = useState(false);
  const [showNow, setShowNow] = useState(false);
  const [welcomeSessionKey, setWelcomeSessionKey] = useState(0);
  const welcomeTimerRef = useRef(null);

  const activeQuestionIdRef = useRef(null);
  const startTimeTokenRef = useRef(null);
  const lastWsQidRef = useRef(null);
  const lastFinalQidRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef({ tries: 0, timer: null });

  useEffect(() => {
    startTimeTokenRef.current = startTimeToken;
  }, [startTimeToken]);

  const getServerNow = useCallback(async () => {
    const res = await axios.get(API_ENDPOINT.time.getNow);
    const now = Number(res?.data?.server_time);
    return now;
  }, []);

  const createTimeToken = useCallback(async (now) => {
    const res = await axios.post(API_ENDPOINT.time.postCreateTime, {
      game_id: "quiz",
      time_type: "progress",
      time: now,
    });
    const token = res?.data?.token ?? null;
    return token;
  }, []);

  const createStartTimeToken = useCallback(async () => {
    const now = await getServerNow();
    const token = await createTimeToken(now);
    setStartTimeToken(token);
    console.log("[API] startTimeToken created", { token, now });
    return token;
  }, [getServerNow, createTimeToken]);

  const createCurrentTimeToken = useCallback(async () => {
    const now = await getServerNow();
    const token = await createTimeToken(now);
    setCurrentTimeToken(token);
    console.log("[API] currentTimeToken created", { token, now });
    return token;
  }, [getServerNow, createTimeToken]);

  const fetchUserAnswers = useCallback(async (qid) => {
    if (!qid) return;
    try {
      const res = await axios.get(
        API_ENDPOINT.wallQuestions.getResultByQuestion(qid)
      );
      const data = res?.data?.data ?? res?.data ?? [];
      const list = Array.isArray(data) ? data : [];
      setUserAnswers(list);
      console.log("[API] fetched user answers", { qid, count: list.length });
    } catch (e) {
      console.log("[API] fetchUserAnswers error", e);
      setUserAnswers([]);
    }
  }, []);

  const sendFinalResult = useCallback(
    async ({ startToken, currentToken, qid }) => {
      if (!qid || !startToken || !currentToken) {
        console.log("[FINAL] skipping: missing params", {
          qid,
          startToken: !!startToken,
          currentToken: !!currentToken,
        });
        setFinished(true);
        fetchUserAnswers(qid);
        return;
      }
      const url = API_ENDPOINT.wallQuestions.getFinalShow.endsWith("/")
        ? `${API_ENDPOINT.wallQuestions.getFinalShow}${qid}`
        : `${API_ENDPOINT.wallQuestions.getFinalShow}/${qid}`;
      console.log("[FINAL] fetching", { url, qid });
      const res = await axios.get(url, {
        params: {
          start_time_token: startToken,
          current_time_token: currentToken,
        },
      });
      const finalData = res?.data?.data ?? res?.data ?? null;
      console.log("[FINAL] response", { hasData: !!finalData });
      if (finalData) {
        setQuestion(finalData);
        setFinished(true);
        fetchUserAnswers(qid);
      }
    },
    [fetchUserAnswers]
  );

  const fetchQuestionData = useCallback(
    async (qid) => {
      const id = Number(qid);
      if (!id) {
        console.log("[QUESTION] invalid qid", qid);
        return;
      }

      // [FIX] compute using refs; don't depend on state to avoid effect churn
      const hasCurrent = !!questionRef.current && !!selectedIdRef.current;

      if (!hasCurrent) {
        if (welcomeTimerRef.current) clearTimeout(welcomeTimerRef.current);
        setWelcomeSessionKey((k) => k + 1);
        setCanReveal(false);
        setShowNow(false);
      }

      setErr(null);
      const instanceKey = Symbol("q");
      activeQuestionIdRef.current = instanceKey;
      setLoading(true);

      try {
        const url = API_ENDPOINT.wallQuestions.getShow.endsWith("/")
          ? `${API_ENDPOINT.wallQuestions.getShow}${id}`
          : `${API_ENDPOINT.wallQuestions.getShow}/${id}`;

        const res = await axios.get(url);
        const data = res?.data?.data ?? res?.data ?? null;

        if (activeQuestionIdRef.current !== instanceKey) {
          console.log("[QUESTION] stale fetch discarded", { id });
          return;
        }

        console.log("[QUESTION] fetched", {
          id,
          hasData: !!data,
          duration: data?.duration,
        });

        setFinished(false);
        setHighlightedAnswerId(null);
        lastFinalQidRef.current = null;

        // ✅ Start the countdown immediately; don't block on token API
        const durationSec = Math.max(0, Number(data?.duration ?? 0));
        start(durationSec);

        // Fire-and-forget: create start token in background
        createStartTimeToken()
          .then((token) => {
            if (activeQuestionIdRef.current !== instanceKey) {
              return;
            }
            setStartTimeToken(token);
          })
          .catch((e) => console.log("createStartTimeToken failed:"));

        setQuestion(data);
        setSelectedId(id);

        if (!hasCurrent) {
          welcomeTimerRef.current = setTimeout(() => {
            setCanReveal(true);
            welcomeTimerRef.current = null;
          }, WELCOME_DELAY_MS);
        }
      } catch (e) {
        if (activeQuestionIdRef.current !== instanceKey) return;
        console.log("[QUESTION] error", e);
        setErr(e?.message || "Failed to load question");
      } finally {
        if (activeQuestionIdRef.current === instanceKey) {
          setLoading(false);
        }
      }
    },
    [createStartTimeToken, start] // [FIX] removed question/selectedId deps
  );

  const resetForNewQuestion = useCallback(
    (qid) => {
      if (!qid) return;
      if (qid === lastWsQidRef.current) {
        // Heartbeats often repeat the same qid — ignore
        return;
      }
      console.log("[WS] switching to qid", { qid });
      lastWsQidRef.current = qid;
      stop();
      setCurrentTimeToken(null);
      setStartTimeToken(null);
      setFinished(false);
      setShowUserAnswers(false);
      setUserAnswers([]);
      fetchQuestionData(qid);
    },
    [fetchQuestionData, stop]
  );

  // [FIX] Use a ref to keep the latest onmessage handler without restarting WS
  const handleWsMessageRef = useRef(null);
  useEffect(() => {
    handleWsMessageRef.current = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        const t = (msg?.type || "").toLowerCase();
        const event = (msg?.data?.event || "").toLowerCase();
        const qid = Number(msg?.data?.question_id ?? msg?.data?.questionId);

        console.log("[WS] message", { type: t, event, qid });

        if (t === "heartbeat" && event === "question_done") {
          if (!qid) return;
          if (lastFinalQidRef.current === qid) {
            console.log("[WS] final already fetched", { qid });
            return;
          }
          lastFinalQidRef.current = qid;
          stop();
          (async () => {
            try {
              const endToken = await createCurrentTimeToken();
              await sendFinalResult({
                startToken: startTimeTokenRef.current,
                currentToken: endToken,
                qid,
              });
            } catch (e) {
              console.log("[FINAL] error", e);
            }
          })();
          return;
        }

        if (
          (t === "update" || t === "heartbeat") &&
          event === "question_started"
        ) {
          if (qid) resetForNewQuestion(qid);
          return;
        }

        if (t === "init" || (t === "heartbeat" && !event)) {
          if (qid) resetForNewQuestion(qid);
          else console.log("[WS] init/heartbeat without qid");
          return;
        }

        if (t === "highlight" || t === "answer_marked") {
          const aid = Number(
            msg?.data?.answer_id ??
              msg?.data?.answerId ??
              msg?.payload?.answerId
          );
          console.log("[WS] highlight/answer_marked", { aid });
          if (aid) setHighlightedAnswerId(aid);
          return;
        }

        console.log("[WS] unhandled", msg);
      } catch (e) {
        console.log("[WS] parse error", e, ev?.data);
      }
    };
  }, [resetForNewQuestion, stop, createCurrentTimeToken, sendFinalResult]);

  // [FIX] WS connection effect runs ONCE; no more stop() due to re-renders
  useEffect(() => {
    if (!WS_URL) {
      console.log("[WS] disabled: WS_URL missing");
      return;
    }

    const urlObj = new URL(WS_URL);
    urlObj.searchParams.set("token", WS_TOKEN);
    const fullUrl = urlObj.toString();

    const connect = () => {
      try {
        console.log("[WS] connecting", { url: fullUrl });
        const ws = new WebSocket(fullUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log("[WS] open");
          reconnectRef.current.tries = 0;
          if (reconnectRef.current.timer) {
            clearTimeout(reconnectRef.current.timer);
            reconnectRef.current.timer = null;
          }
        };

        ws.onmessage = (ev) =>
          handleWsMessageRef.current && handleWsMessageRef.current(ev);

        ws.onerror = (errEvt) => {
          console.log("[WS] error event", errEvt);
        };

        ws.onclose = (ev) => {
          console.log("[WS] close", {
            code: ev.code,
            reason: ev.reason,
            wasClean: ev.wasClean,
          });
          wsRef.current = null;
          const tries = (reconnectRef.current.tries || 0) + 1;
          reconnectRef.current.tries = tries;
          const delay = Math.min(1000 * 2 ** Math.min(tries, 5), 15000);
          console.log("[WS] reconnecting in", delay, "ms");
          reconnectRef.current.timer = setTimeout(connect, delay);
        };
      } catch (e) {
        console.log("[WS] connect threw", e);
        reconnectRef.current.timer = setTimeout(connect, 2000);
      }
    };

    connect();

    const ping = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(JSON.stringify({ type: "ping" }));
          console.log("[WS] ping");
        } catch (e) {
          console.log("[WS] ping failed", e);
        }
      }
    }, 25000);

    return () => {
      clearInterval(ping);
      if (reconnectRef.current.timer) {
        clearTimeout(reconnectRef.current.timer);
        reconnectRef.current.timer = null;
      }
      if (wsRef.current) {
        try {
          console.log("[WS] closing on unmount");
          wsRef.current.close();
        } catch {}
        wsRef.current = null;
      }
      stop(); // only on real unmount
    };
  }, []); // IMPORTANT: no deps, runs once

  useOperatorActions("DAY2_MARK_ANSWER", (action) => {
    const aId = action?.payload?.answerId;
    console.log("[OP] DAY2_MARK_ANSWER", { aId });
    if (aId != null) setHighlightedAnswerId(Number(aId));
  });

  useOperatorActions("DAY2_SHOW_USER_ANSWERS", (action) => {
    const qid = action?.payload?.questionId;
    if (qid && qid === selectedIdRef.current) {
      console.log("[OP] SHOW_USER_ANSWERS", { qid });
      setShowUserAnswers(true);
    }
  });
  useOperatorActions("DAY2_HIDE_USER_ANSWERS", (action) => {
    const qid = action?.payload?.questionId;
    if (!qid || qid === selectedIdRef.current) {
      console.log("[OP] HIDE_USER_ANSWERS", { qid });
      setShowUserAnswers(false);
    }
  });

  const hasCurrentQuestion = !!question && !!selectedId;
  const showWelcome = !hasCurrentQuestion;

  if (showUserAnswers && selectedId) {
    return (
      <QuesionAnswersComponent questionId={selectedId} users={userAnswers} />
    );
  }

  if (showWelcome) {
    return (
      <div className="relative w-full flex items-center justify-center">
        <motion.div
          key={`q-body-${welcomeSessionKey}`}
          initial={{ opacity: 0, y: 8, scale: 0.98, filter: "blur(3px)" }}
          animate={
            showNow
              ? { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }
              : { opacity: 0, y: 8, scale: 0.98, filter: "blur(3px)" }
          }
          transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
          aria-hidden={!showNow}
          className={`w-full flex justify-center ${
            showNow ? "pointer-events-auto" : "pointer-events-none"
          }`}
          style={
            showNow
              ? {}
              : {
                  position: "absolute",
                  inset: 0,
                  visibility: "hidden",
                }
          }
        />
        <AnimatePresence
          mode="wait"
          onExitComplete={() => setShowNow(true)}
          initial={false}
        >
          {!canReveal && (
            <motion.div
              key="welcome-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="relative z-10"
            >
              <WelcomeComponent text="پرسش و پاسخ" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={`question-${selectedId}`}
        initial={{ opacity: 0, y: 12, scale: 0.99 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -8, scale: 0.995 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        dir="rtl"
        className="w-full flex flex-col gap-[75px] items-center justify-center text-white"
      >
        {!finished && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25 }}
            className="absolute left-[30%] top-[4%] -translate-x-1/2 -translate-y-1/2"
          >
            <div
              className="text-[56px] font-[900]"
              style={{ color: secondsLeft < 6 ? "#EF3200" : "#D4D4D4" }}
            >
              <motion.span
                key={secondsLeft}
                initial={{ scale: 1 }}
                animate={{ scale: [1, 1.18, 1] }}
                transition={{ duration: 0.28, ease: "easeOut" }}
                className="inline-block"
              >
                {Number.isFinite(secondsLeft) ? secondsLeft : 0}
              </motion.span>
              s
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="question-container w-full max-w-[815px] max-h-[384px] h-full relative"
        >
          <div className="w-full max-w-[815px] max-h-[384px] h-full flex items-center justify-center">
            <div className="top-0 left-0 w-full h-full relative flex items-center justify-center">
              <div>
                <svg
                  width="665"
                  height="313"
                  viewBox="0 0 665 313"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M471.992 309.713H207.986L182.968 284.695H50.2574L43.1092 291.843H20.2008L9.79428 281.437V202.58L0 192.786V57.5599L9.39921 48.1604V13.7158H40.7265L50.853 23.8425H285.423L307.761 1.50488H648.82V135.145L662.819 149.144V276.775L648.277 289.46H492.245L471.992 309.713ZM208.706 307.976H471.273L491.526 287.723H647.626L661.082 275.986V149.863L647.083 135.865V3.24225H308.481L286.143 25.5798H50.1337L40.0076 15.4531H11.1373V48.88L1.73814 58.2792V192.066L11.5324 201.86V280.717L20.9211 290.106H42.3902L49.5382 282.958H183.688L208.706 307.976Z"
                    fill="#A9A9A9"
                  />
                  <path
                    d="M217.224 298.852L202.977 284.604H477.457L463.658 298.404L463.299 298.404L217.224 298.852ZM207.171 286.342L217.942 297.113L462.936 296.668L473.262 286.342H207.171Z"
                    fill="#A9A9A9"
                  />
                  <path
                    d="M463.297 297.535L475.359 285.473H350.914L363.423 297.982L463.297 297.535Z"
                    fill="#A9A9A9"
                  />
                  <path
                    d="M41.4071 283.386L16.2402 258.219V252.113L41.5562 277.429L41.4071 283.386Z"
                    fill="#EF3200"
                  />
                  <path
                    d="M16.2383 275.87V269.765L31.8746 285.401H25.7696L16.2383 275.87Z"
                    fill="#EF3200"
                  />
                  <path
                    d="M41.4071 265.519L16.2402 240.351V234.246L41.5562 259.562L41.4071 265.519Z"
                    fill="#EF3200"
                  />
                  <path
                    d="M41.4071 247.649L16.2402 222.482V216.376L41.5562 241.692L41.4071 247.649Z"
                    fill="#EF3200"
                  />
                  <path
                    d="M41.4071 229.78L16.2402 204.613V198.507L41.5562 223.823L41.4071 229.78Z"
                    fill="#EF3200"
                  />
                  <path
                    d="M41.4071 211.907L16.2402 186.741V180.635L41.5562 205.951L41.4071 211.907Z"
                    fill="#EF3200"
                  />
                  <path
                    d="M41.4071 194.041L16.2402 168.874V162.769L41.5562 188.084L41.4071 194.041Z"
                    fill="#EF3200"
                  />
                  <path
                    d="M41.4071 176.168L16.2402 151.001V144.896L41.5562 170.211L41.4071 176.168Z"
                    fill="#EF3200"
                  />
                  <path
                    d="M41.4071 158.3L16.2402 133.133V127.027L41.5562 152.343L41.4071 158.3Z"
                    fill="#EF3200"
                  />
                  <path
                    d="M41.4071 140.429L16.2402 115.262V109.156L41.5562 134.472L41.4071 140.429Z"
                    fill="#EF3200"
                  />
                  <path
                    d="M41.4071 122.557L16.2402 97.3899V91.2842L41.5562 116.6L41.4071 122.557Z"
                    fill="#EF3200"
                  />
                  <path
                    d="M41.4071 104.689L16.2402 79.5215V73.416L41.5562 98.7319L41.4071 104.689Z"
                    fill="#EF3200"
                  />
                  <path
                    d="M41.4071 86.8175L16.2402 61.6507V55.5449L41.5562 80.8608L41.4071 86.8175Z"
                    fill="#EF3200"
                  />
                  <path
                    d="M41.4071 68.9493L16.2402 43.7822V37.6768L41.5562 62.9926L41.4071 68.9493Z"
                    fill="#EF3200"
                  />
                  <path
                    d="M41.4071 51.0782L16.2402 25.9114V19.8057L41.5562 45.1215L41.4071 51.0782Z"
                    fill="#EF3200"
                  />
                  <path
                    d="M28.0059 19.8047H34.1107L41.5574 27.2511L41.4077 33.2067L28.0059 19.8047Z"
                    fill="#EF3200"
                  />
                  <path
                    d="M647.948 76.8405C646.797 76.8405 645.863 75.9068 645.863 74.7556V27.4003C645.863 26.2491 646.797 25.3154 647.948 25.3154C649.099 25.3154 650.033 26.2491 650.033 27.4003V74.7556C650.033 75.9068 649.099 76.8405 647.948 76.8405Z"
                    fill="#A9A9A9"
                  />
                  <path
                    d="M598.659 290.386H539.241C538.09 290.386 537.156 289.453 537.156 288.302C537.156 287.15 538.09 286.217 539.241 286.217H598.659C599.81 286.217 600.744 287.15 600.744 288.302C600.744 289.453 599.81 290.386 598.659 290.386Z"
                    fill="#A9A9A9"
                  />
                  <path
                    d="M290.397 2.08301L278.334 14.1452H55.9999L43.9922 2.13755L290.397 2.08301Z"
                    fill="#A9A9A9"
                  />
                  <path
                    d="M546.839 4.16967H400.304C399.152 4.16967 398.219 3.23636 398.219 2.08484C398.219 0.933312 399.152 0 400.304 0H546.839C547.99 0 548.924 0.933312 548.924 2.08484C548.924 3.23636 547.99 4.16967 546.839 4.16967Z"
                    fill="#A9A9A9"
                  />
                  <path
                    d="M191.521 293.811C190.987 293.811 190.454 293.608 190.047 293.2L182.467 285.621H122.423C121.272 285.621 120.338 284.687 120.338 283.536C120.338 282.385 121.272 281.451 122.423 281.451H183.33C183.883 281.451 184.413 281.671 184.804 282.062L192.995 290.252C193.809 291.066 193.809 292.386 192.995 293.2C192.588 293.608 192.055 293.811 191.521 293.811Z"
                    fill="#A9A9A9"
                  />
                  <g opacity="0.21">
                    <path
                      d="M664.828 286.62L655.245 296.204H502.785L488.906 310.082H664.828V286.62Z"
                      fill="#01001A"
                    />
                  </g>
                  <g opacity="0.21">
                    <path
                      d="M191.517 308.557L176.328 293.367H57.8386L49.9885 301.217H10.8906V312.999H195.91L191.517 308.557Z"
                      fill="#01001A"
                    />
                  </g>
                </svg>
              </div>
            </div>
            <div className="text-content absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <span dir="rtl" className="text-[22px] text-white font-normal">
                {question?.question}
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={listVariants}
          initial="hidden"
          animate="show"
          className="w-screen flex items-center justify-center"
        >
          <div className="flex items-center flex-row justify-center gap-[58px] flex-wrap w-[1150px]">
            {Array.isArray(question?.answers) && question.answers.length > 0 ? (
              question.answers.map((ans) => (
                <motion.div key={ans.id} variants={itemVariants}>
                  <AnswerContainerComponent
                    state={
                      finished
                        ? ans.is_correct
                          ? "correct"
                          : "wrong"
                        : ans.id === highlightedAnswerId
                        ? "selected"
                        : "default"
                    }
                    text={ans.answer}
                  />
                </motion.div>
              ))
            ) : (
              <motion.div
                variants={itemVariants}
                className="text-white/60 text-sm"
              >
                No answers.
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
